import agencyApi from 'api/agencyApi';
import imgEmptyNoData from 'assets/images/img_emptyview_no_data.png';
import AgencyHelper, { AgencyConstants } from 'features/Agency/agencyHelper';
import ChargeHelper from 'features/Charge/chargeHelper';
import DealerHelper from 'features/ManageDealer/dealerHelper';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import BreadcrumbHelper from 'general/components/CustomBreadcrumb/BreadcrumbHelper';
import DropdownFilterTable from 'general/components/DropdownFilterTable';
import DropdownTableConfigColumn from 'general/components/DropdownTableConfigColumn';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTBSButton, {
  KTBSButtonColor,
  KTBSButtonSize,
} from 'general/components/OtherKeenComponents/KTBSButton';
import KTBSCard from 'general/components/OtherKeenComponents/KTBSCard';
import KTDropdownFilter from 'general/components/OtherKeenComponents/KTDropdownFilter';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTPagination, {
  KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import CustomDateRangePicker, { dateFilter, getDateFilterOption } from 'general/components/Others/CustomDateRangePicker';
import EmptyView from 'general/components/Others/EmptyView';
import MenuActionDropdown from 'general/components/Others/MenuActionDropdown';
import AppColors from 'general/constants/AppColors';
import AppConstants from 'general/constants/AppConstants';
import ToastHelper from 'general/helpers/ToastHelper';
import Global from 'general/utils/Global';
import Utils from 'general/utils/Utils';
import useAppConfig from 'hooks/useAppConfig';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import useRouter from 'hooks/useRouter';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as ExcelJS from 'exceljs';
import UIHelper from 'general/helpers/UIHelper';
import DialogDownloadVoucherPartnerExcelFile from '../DialogDownloadVoucherPartnerExcelFile';
import VoucherPartnerListFilterView from './components/VoucherPartnerListFilterView';
import AppConfigs from 'general/constants/AppConfigs';

const defaultColumnIds = [
  'AGENCY_CODE',
  'AGENCY_NAME',
  'TYPE',
  'PARENT_AGENCY',
  'PHONE',
  'EMAIL',
  'MST/CCCD',
  'STATUS',
  'COUNTRY',
  'ADDRESS',
  'CREATEDBY',
  'DATE',
];

function VoucherPartnerListView(props) {
  // ---- params ----
  const { t } = useTranslation();
  const router = useRouter();
  const [operatorList, setOperatorList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [filter, setFilter] = useState(() => ({ ...Global.g_filterVoucherPartnerList }));
  const [filterDropdown, setFilterDropdown] = useState(() => ({ ...Global.g_filterVoucherPartnerList }));

  // khu vuc
  const areaTree = useSelector((state) => state.app.areaTree);
  const areaProvinceList = useMemo(() => {
    return ChargeHelper.Utils.getAreaListByType([areaTree], 'P');
  }, [areaTree]);
  const areaProvinceOptions = useMemo(() => {
    return _.map(areaProvinceList, (item) => {
      return {
        name: item.name,
        value: item.areaId,
      };
    });
  }, [areaProvinceList]);
  const [selectedAreaProvince, setSelectedAreaProvince] = useState();
  const areaDistrictList = useMemo(() => {
    if (!selectedAreaProvince) return [];
    return ChargeHelper.Utils.getSubAreaListByParentId([areaTree], selectedAreaProvince?.areaId);
  }, [areaTree, selectedAreaProvince]);
  const areaDistrictOptions = useMemo(() => {
    return _.map(areaDistrictList, (item) => {
      return {
        name: item.name,
        value: item.areaId,
      };
    });
  }, [areaDistrictList]);
  const [selectedAreaDistrict, setSelectedAreaDistrict] = useState();
  useEffect(() => {
    const areaId = filterDropdown.areaId;
    if (Utils.isValid(areaId)) {
      const area = ChargeHelper.Utils.getAreaById([areaTree], areaId);
      if (area?.typeCode === 'P') {
        setSelectedAreaProvince(area);
      } else {
        setSelectedAreaDistrict(area);
        const parentAreaId = area?.parentId;
        if (Utils.isValid(parentAreaId)) {
          setSelectedAreaProvince(ChargeHelper.Utils.getAreaById([areaTree], parentAreaId));
        }
      }
    }
  }, [filterDropdown?.areaId, areaTree]);

  const app = useSelector((state) => state.app);
  const { user, permissionList } = useSelector((state) => state.auth);
  const { isGettingData, voucherPartnerList, voucherPartnerTotal } = useSelector((state) => state.agency);
  const adminRole =
    user?.role === AppConstants.userRole.admin || user?.role === AppConstants.userRole.operator;
  const operatorId = useMemo(() => {
    return app?.appConfig?.operators?.[0]?.operatorId ?? null;
  }, [app]);
  const commissionRateSale = useAppConfig()?.commissionRateSale;
  const commissionRateGroup = useAppConfig()?.commissionRateGroup;
  const commissionRateLandlord = useAppConfig()?.commissionRateLandlord;

  const fullColumns = useMemo(() => {
    return [
      {
        id: 'AGENCY_CODE',
        sortable: false,
        name: UIHelper.buildTableColumn('Mã đối tác'),
        width: '120px',
        selector: (row) => row?.agencyCode ?? row.agencyId,
        style: tableStyle.columns.normal,
      },
      {
        id: 'AGENCY_NAME',
        sortable: false,
        name: UIHelper.buildTableColumn('Tên đối tác'),
        selector: (row) => {
          return (
            <KTTooltip offset={[0, 0]} text={row.name ?? ''}>
              <span>{row.name ?? ''}</span>
            </KTTooltip>
          );
        },
        minWidth: '240px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'TYPE',
        sortable: false,
        name: UIHelper.buildTableColumn('Loại đối tác'),
        selector: (row) => t(row.type),
        width: '120px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'PARENT_AGENCY',
        sortable: false,
        name: UIHelper.buildTableColumn('Đối tượng quản lý'),
        selector: (row) => (
          <KTTooltip offset={[0, 0]} text={row?.parentAgency?.name}>
            <span
              className="cursor-pointer hover-opacity-60"
              onClick={() => {
                DealerHelper.navigateToParentAgency(row?.parentAgency);
              }}
            >
              {row?.parentAgency?.name}
            </span>
          </KTTooltip>
        ),
        minWidth: '200px',
        style: tableStyle.columns.link,
      },
      {
        id: 'PHONE',
        sortable: false,
        name: UIHelper.buildTableColumn('Số điện thoại'),
        width: '140',
        selector: (row) => row.phone ?? '',
        style: tableStyle.columns.normal,
      },
      {
        id: 'EMAIL',
        sortable: false,
        name: UIHelper.buildTableColumn('Email'),
        selector: (row) => {
          return (
            <KTTooltip offset={[0, 0]} text={row?.email ?? ''}>
              <span>{row?.email ?? ''}</span>
            </KTTooltip>
          );
        },
        minWidth: '240px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'MST/CCCD',
        sortable: false,
        name: UIHelper.buildTableColumn('MST/CCCD'),
        width: '160px',
        selector: (row) =>
          row.type === AppConstants.accountType.individual
            ? row.idCardNumber?.length > 0
              ? row.idCardNumber
              : row.taxCode?.length > 0
                ? row.taxCode
                : ''
            : row.taxCode?.length > 0
              ? row.taxCode
              : '',
        style: tableStyle.columns.normal,
      },
      {
        id: 'STATUS',
        sortable: false,
        name: UIHelper.buildTableColumn('Trạng thái'),
        width: '150px',
        selector: (row) => {
          return (
            <KTLabel
              text={t(`agencyStatus_${row.status}`)}
              isInline={true}
              size={KTLabelSize.medium}
              additionalClassName={`label-light-${DealerHelper.getAgencyStatusColor(row.status)}`}
            />
          );
        },
      },
      {
        id: 'COUNTRY',
        name: UIHelper.buildTableColumn('Quốc gia'),
        selector: (row) => _.find(AgencyHelper.Data.CountryOptions, { value: row?.country })?.name,
        style: tableStyle.columns.normal,
        width: '150px',
      },
      {
        id: 'ADDRESS',
        sortable: false,
        name: UIHelper.buildTableColumn('Địa chỉ'),
        selector: (row) => {
          const area = ChargeHelper.Utils.getAreaById([areaTree], row?.areaId);
          let parentArea;
          if (area?.typeCode === 'W') {
            parentArea = ChargeHelper.Utils.getAreaById([areaTree], area?.parentId);
          }
          const addressResuslt = [
            row.address,
            area?.name,
            parentArea?.name
          ]
            .filter(Boolean)
            .join(', ');
          return (
            <KTTooltip text={addressResuslt} offset={[0, 0]}>
              <span>{addressResuslt?.length > 0 ? addressResuslt : ''}</span>
            </KTTooltip>
          );
        },
        minWidth: '250px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'CREATEDBY',
        name: UIHelper.buildTableColumn('Người tạo'),
        selector: (row) => {
          return (
            <KTTooltip offset={[0, 0]} text={row?.createdBy?.fullname ?? row?.name}>
              <span
                className="cursor-pointer hover-opacity-60"
                onClick={() => {
                  DealerHelper.navigateToParentAgency(row?.createdBy ?? row);
                }}
              >
                {row?.createdBy?.fullname ?? row?.name}
              </span>
            </KTTooltip>
          );
        },
        style: tableStyle.columns.link,
        sortable: false,
        width: '200px',
      },
      {
        id: 'DATE',
        sortable: false,
        name: UIHelper.buildTableColumn('Ngày tạo'),
        selector: (row) => row?.createdAt ? Utils.formatDateTimeToString(row?.createdAt, 'DD/MM/YYYY - HH:mm') : '',
        style: tableStyle.columns.normal,
        width: '180px',
      },
      {
        name: UIHelper.buildTableColumn('Thao tác'),
        width: '130px',
        wrap: true,
        style: {
          position: 'sticky',
          right: 0,
          backgroundColor: '#fff',
          borderLeft: '1px solid #ebedfe',
        },
        cell: (row) => {
          return (
            <MenuActionDropdown
              menuAction={[
                {
                  iconElement: <i className="fa-regular fa-eye" style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}></i>,
                  text: t('Xem chi tiết'),
                  click: () => {
                    handleClickedRow(row);
                  },
                  show: Utils.checkPermission(permissionList, AppConstants.permissionCode.voucherPartnerView?.code),
                },
                {
                  iconElement: <i className="fa-regular fa-bolt" style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}></i>,
                  text: t('Kích hoạt tài khoản'),
                  click: () => {
                    DealerHelper.handleAcceptAgency(row, () => {
                      getVoucherPartnerList(filter);
                    });
                  },
                  show: row?.status === 'REGISTERED'
                    && (adminRole ||
                      DealerHelper.checkHasEditPermission(
                        row,
                        row?.parentAgency,
                        user?.agency
                      ))
                    && Utils.checkPermission(permissionList, AppConstants.permissionCode.voucherPartnerUpdate?.code),
                },
                {
                  iconElement: <i className="fa-regular fa-pen-to-square" style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}></i>,
                  text: t('edit'),
                  click: () => {
                    handleShowEditVoucherPartner(row);
                  },
                  show: (!row?.numberOfSignedContracts
                    && DealerHelper.checkHasEditPermission(
                      row,
                      row?.parentAgency,
                      user?.agency
                    ) || adminRole)
                    && Utils.checkPermission(permissionList, AppConstants.permissionCode.voucherPartnerUpdate?.code),
                },
                {
                  iconElement: <i className="far fa-square" style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}></i>,
                  text: t('Ngừng hoạt động'),
                  click: () => {
                    DealerHelper.handleStopAgency(row, () => {
                      getVoucherPartnerList(filter);
                    });
                  },
                  show: row?.status === 'NORMAL'
                    && (adminRole || DealerHelper.checkHasEditPermission(
                      row,
                      row?.parentAgency,
                      user?.agency
                    ))
                    && Utils.checkPermission(permissionList, AppConstants.permissionCode.voucherPartnerUpdate?.code),
                },
                {
                  iconElement: <i className="far fa-play" style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}></i>,
                  text: t('Khôi phục hoạt động'),
                  click: () => {
                    DealerHelper.handleActiveAgency(row, () => {
                      getVoucherPartnerList(filter);
                    });
                  },
                  show: row?.status === 'SUSPENDED'
                    && (adminRole || DealerHelper.checkHasEditPermission(
                      row,
                      row?.parentAgency,
                      user?.agency
                    ))
                    && Utils.checkPermission(permissionList, AppConstants.permissionCode.voucherPartnerUpdate?.code),
                },
                {
                  iconElement: <i className={`fa-regular fa-lock`} style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}></i>,
                  text: t('Đặt lại mật khẩu'),
                  click: () => {
                    DealerHelper.showDialogChangePassword(row, () => {
                      getVoucherPartnerList(filter);
                    })
                  },
                  show: adminRole && Utils.checkPermission(permissionList, AppConstants.permissionCode.voucherPartnerUpdate?.code),
                },
                {
                  iconElement: <i className={`fa-regular fa-trash-can`} style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}></i>,
                  text: t('Xóa đối tác'),
                  click: () => {
                    DealerHelper.handleDeleteAgency(row, () => {
                      getVoucherPartnerList(filter);
                    });
                  },
                  show: !row?.numberOfSignedContracts
                    && (adminRole || DealerHelper.checkHasEditPermission(
                      row,
                      row?.parentAgency,
                      user?.agency
                    ))
                    && Utils.checkPermission(permissionList, AppConstants.permissionCode.voucherPartnerDelete?.code),
                },
              ]}
            />
          );
        },
      },
    ];
  }, [t, operatorList, areaTree, user, filter]);

  const prefKey = useMemo(() => {
    const userId = user.userId;
    const pathName = router.pathname;
    const key = `${userId}_${pathName}`;
    return key;
  }, [user, router]);
  const [fullColumnIds, setFullColumnIds] = useState(fullColumns?.map((row) => row?.id));
  const {
    columnIds,
    columns,
    onChangeCheckboxValue,
    saveChanges,
    resetDefaultTableColumn,
    onSelectAll,
    onUnSelectAll,
    fullColumnsSorted,
    fullColumnsIdSorted,
  } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);

  useEffect(() => {
    if (app.areaTree) {
      classifyAreas(app.areaTree);
    }
  }, [app.areaTree]);
  const classifyAreas = (area) => {
    if (!area) return;

    if (area.parentId === 0) {
      setCityList([
        {
          areaId: area.areaId,
          name: area.name,
          districtList: area.subAreas?.map((subArea) => ({
            areaId: subArea.areaId,
            name: subArea.name,
          })),
        },
      ]);
    }
  };

  const classifyOperators = (operators) => {
    setOperatorList(
      operators?.map((operator) => ({ operatorId: operator.operatorId, name: operator.name }))
    );
  };

  useEffect(() => {
    if (app.appConfig) {
      classifyOperators(app.appConfig.operators ?? []);
    }
  }, [app.appConfig]);

  const parentAgencyId = adminRole ? '' : user?.agency?.agencyId;

  useEffect(() => {
    try {
      DealerHelper.callApiGetAgencyList({ ...filter, parentAgencyId });
    } catch (error) {
      ToastHelper.showError(error?.response?.data?.reason);
    }
  }, [filter, parentAgencyId]);

  const getVoucherPartnerList = () => {
    try {
      DealerHelper.callApiGetAgencyList({ ...filter, parentAgencyId });
    } catch (error) {
      ToastHelper.showError(error?.response?.data?.reason);
    }
  };


  // ---- methods ----
  const handleClickedRow = (row) => {
    BreadcrumbHelper.setBreadcrumPath(t('voucherPartnerDetail'), `${router?.pathname}/${row.agencyId}`);
    router.navigate(`${row.agencyId}`);
  };

  //them doi tac
  const handleOpenAddVoucherPartnerModal = () => {
    BreadcrumbHelper.setBreadcrumPath(t('Thêm đối tác'), `${router?.pathname}/add`);
    router.navigate('add');
  };

  // chinh sua doi tac
  const handleShowEditVoucherPartner = (row) => {
    BreadcrumbHelper.setBreadcrumPath(t('Chỉnh sửa đối tác'), `${router?.pathname}/${row?.agencyId}/edit`);
    router.navigate(`${row?.agencyId}/edit`);
  };

  const handleCreateSaleCommission = async (row) => {
    await DealerHelper.callApiCreateAgencyCommission(
      Utils.transformEmptyStrings({
        operatorId: operatorId,
        agencyId: row?.agencyId,
        role: 'SALE',
        energyCommissionMode: AppConstants.energyCommissionMode.fixedPerKwh,
        energyCommissionValue: 0,
        energyCommissionDuration: null,
        serviceCommission: 0,
        saleCommission: [
          {
            revenue: 0,
            discount: Number(commissionRateSale),
          },
        ],
        packageCommission: [
          {
            revenue: 0,
            discount: Number(commissionRateGroup),
          },
        ],
        landlordCommission: JSON.parse(commissionRateLandlord),
      })
    );
    getVoucherPartnerList();
  };

  const updateFilter = (key, value) => {
    const patch = typeof key === 'object' ? key : { [key]: value };
    const nextFilter = { ...Global.g_filterVoucherPartnerList, ...patch };
    Global.g_filterVoucherPartnerList = nextFilter;
    setFilter({ ...nextFilter });
    setFilterDropdown({ ...nextFilter });
  };

  // xuat file excel
  const handleExportExcel = async (exportData, params) => {
    let exportDataList = exportData;
    if (_.isEmpty(exportDataList)) {
      ToastHelper.showError('Không có dữ liệu để tải xuống');
      return;
    }

    const sheetFileName = 'Danh_sách_đối_tác_mua_voucher_' + moment(params.from).format('DDMMYYYY') + '_' + moment(params.to).format('DDMMYYYY');
    const ext = '.xlsx';
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet(sheetFileName, {
      headerFooter: { firstHeader: sheetFileName },
    });

    sheet.mergeCells('A1:M1');
    sheet.getCell('A1').value = `DANH SÁCH ĐỐI TÁC MUA VOUCHER (NGÀY ${moment(params.from).format(
      'DD/MM/YYYY'
    )} - ${moment(params.to).format('DD/MM/YYYY')})`;
    sheet.getCell('A1').font = {
      name: 'Times New Roman',
      family: 4,
      size: 11,
      bold: true,
      color: { argb: 'FFFFFFFF' },
    };
    sheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };
    sheet.getCell('A1').fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '29618E' },
      color: { argb: 'FFFFFF' },
    };
    sheet.getCell('A1').border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
    sheet.getRow(1).height = 25;

    const columns = [
      { key: 'stt', width: 7 },
      { key: 'agencyCode', width: 18 },
      { key: 'agencyName', width: 35 },
      { key: 'type', width: 18 },
      { key: 'parentAgency', width: 22 },
      { key: 'phone', width: 22 },
      { key: 'email', width: 30 },
      { key: 'cccd', width: 22 },
      { key: 'status', width: 20 },
      { key: 'country', width: 18 },
      { key: 'address', width: 35 },
      { key: 'createBy', width: 22 },
      { key: 'date', width: 22 },
    ];
    sheet.columns = columns;

    sheet.getRow(2).height = 30;
    sheet.getRow(2).font = {
      name: 'Times New Roman',
      family: 4,
      size: 11,
      bold: true,
    };
    sheet.getRow(2).alignment = {
      vertical: 'middle',
      horizontal: 'center',
      wrapText: true,
    };

    sheet.getCell('A2').value = 'STT';
    sheet.getCell('B2').value = 'Mã đối tác';
    sheet.getCell('C2').value = 'Tên đối tác';
    sheet.getCell('D2').value = 'Loại đối tác';
    sheet.getCell('E2').value = 'Người quản lý';
    sheet.getCell('F2').value = 'Số điện thoại';
    sheet.getCell('G2').value = 'Email';
    sheet.getCell('H2').value = 'MST/CCCD';
    sheet.getCell('I2').value = 'Trạng thái';
    sheet.getCell('J2').value = 'Quốc gia';
    sheet.getCell('K2').value = 'Địa chỉ';
    sheet.getCell('L2').value = 'Người tạo';
    sheet.getCell('M2').value = 'Ngày tạo';


    ['A2:M2'].forEach((range) => {
      sheet.getRow(range.split(':')[0].slice(1)).eachCell({ includeEmpty: true }, (cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
    });

    let i = 1;
    exportDataList?.forEach((row) => {
      // Dia chi
      const area = ChargeHelper.Utils.getAreaById([areaTree], row?.areaId);
      let parentArea;
      if (area?.typeCode === 'W') {
        parentArea = ChargeHelper.Utils.getAreaById([areaTree], area?.parentId);
      }
      const addressResuslt = [
        row.address,
        area?.name,
        parentArea?.name
      ]
        .filter(Boolean)
        .join(', ');

      const dataRow = sheet.addRow({
        stt: i++,
        agencyCode: row?.agencyCode ?? row.agencyId,
        agencyName: row?.name,
        type: t(row.type),
        parentAgency: row?.parentAgency?.name ?? '',
        phone: row.phone ?? '',
        email: row?.email ?? '',
        cccd: row.type === AppConstants.accountType.individual
          ? row.idCardNumber?.length > 0
            ? row.idCardNumber
            : row.taxCode?.length > 0
              ? row.taxCode
              : ''
          : row.taxCode?.length > 0
            ? row.taxCode
            : '',
        status: t(`agencyStatus_${row.status}`),
        country: _.find(AgencyHelper.Data.CountryOptions, { value: row?.country })?.name ?? '',
        address: addressResuslt?.length > 0 ? addressResuslt : '',
        createBy: row?.createdBy?.fullname ?? '',
        date: row?.createdAt ? Utils.formatDateTimeToString(row?.createdAt, 'DD/MM/YYYY - HH:mm') : '',
      });

      dataRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
        if (colNumber === 1) {
          cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        } else {
          cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
        }
      });
    });

    sheet.eachRow((row, rowNumber) => {
      if (rowNumber >= 3) {
        row.font = {
          name: 'Times New Roman',
          family: 4,
          size: 11,
          bold: false,
        };
      }
    });

    workbook.xlsx.writeBuffer().then(function (data) {
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = sheetFileName + ext;
      anchor.click();
      window.URL.revokeObjectURL(url);
      ToastHelper.showSuccess('Tải xuống file Excel thành công');
      AppCustomDialogHelper.hide();
    });
  };

  return (
    <div className=''>
      {UIHelper.buildCard(
        <div className="pb-4">
          {/* header */}
          <div className="px-4 pt-4 pb-4 d-flex align-items-center justify-content-between">
            <div className="d-flex flex-column">
              <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                Danh sách đối tác mua voucher
              </span>
              <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                Tổng:{' '}
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}>
                  {voucherPartnerTotal != 0 ? voucherPartnerTotal : voucherPartnerList?.length} đối tác
                </span>
              </span>
            </div>
            <div>
              {adminRole && (
                <div className="d-flex align-items-center justify-content-end gap-3 flex-wrap">
                  {UIHelper.buildBlueButton(
                    'Tải xuống',
                    async () => {
                      try {
                        AppCustomDialogHelper.show(
                          'Tải xuống',
                          <DialogDownloadVoucherPartnerExcelFile
                            key={Date.now()}
                            filter={{ ...Global.g_filterVoucherPartnerList }}
                            onDownload={async (params) => {
                              try {
                                const { agencies = [] } = await agencyApi.getAgencyList(params);
                                return handleExportExcel(agencies, params);
                              } catch (error) {
                                ToastHelper.showError(`Lỗi khi lấy dữ liệu: ${error.message}`);
                                return [];
                              }
                            }}
                          />,
                          'md',
                          false,
                          true
                        );
                      } catch (error) {
                        ToastHelper.showError(`Lỗi khi khởi tạo tải xuống: ${error.message}`);
                      }
                    },
                    <i className="fa-regular fa-download" />
                  )}
                  {UIHelper.buildGreenButton(
                    'Thêm đối tác',
                    handleOpenAddVoucherPartnerModal,
                    <i className="fa-regular fa-circle-plus" style={{ fontSize: 15 }} />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* table */}
          <div className="">
            {/* filter */}
            <div className="row m-0 p-0 px-2">
              {/* tim kiem */}
              <div className="col mb-4 px-2 pt-1">
                {UIHelper.buildFormLabel('Tìm kiếm')}
                <KTFormSearch
                  name="search-bar"
                  placeholder={t('Theo mã đối tác, tên đối tác, số điện thoại, email, mã số thuế/CCCD')}
                  value={Global.g_filterVoucherPartnerList.q}
                  onSubmit={(text) => {
                    Global.g_filterVoucherPartnerList = {
                      ...Global.g_filterVoucherPartnerList,
                      q: text,
                      page: 0,
                    };
                    setFilter(Global.g_filterVoucherPartnerList);
                    setFilterDropdown(Global.g_filterVoucherPartnerList);
                  }}
                />
              </div>
              {/* loai doi tac */}
              <div className="col mb-4 px-2">
                <KTDropdownFilter
                  label={'Loại đối tác'}
                  filterKey="type"
                  options={DealerHelper.filterAgencyTypeOptions()}
                  selectedValue={Global.g_filterVoucherPartnerList.type}
                  handleChange={(value) => {
                    updateFilter('type', value);
                    updateFilter('page', 0);
                  }}
                />
              </div>
              {/* trang thai */}
              <div className="col mb-4 px-2">
                <KTDropdownFilter
                  label={t('status')}
                  filterKey="status"
                  options={DealerHelper.filterStatusOptionsLandlord()}
                  selectedValue={Global.g_filterVoucherPartnerList.status}
                  handleChange={(value) => {
                    updateFilter('status', value);
                    updateFilter('page', 0);
                  }}
                />
              </div>
              {/* khoang thoi gian */}
              <div className="col mb-4 pt-2 px-2">
                <label htmlFor="filterByDate" className="mb-0 mr-2 font-weight-bolder">
                  Khoảng thời gian
                </label>
                <CustomDateRangePicker
                  toggleElementBorderRadius={5}
                  alignRight={true}
                  initialValue={
                    Global.g_filterVoucherPartnerList.selectedValue?.length > 0
                      ? Global.g_filterVoucherPartnerList.selectedValue
                      : dateFilter.tatCaThoiGian
                  }
                  onPressSave={(selectedValue, startDate, endDate) => {
                    updateFilter('from', startDate);
                    updateFilter('to', endDate);
                    updateFilter('selectedValue', selectedValue);
                    updateFilter('page', 0);
                  }}
                />
              </div>

              <div className="col mb-4 p-2 d-flex align-items-end gap-3">
                {/* bo loc */}
                <div style={{ minWidth: 95 }}>
                  <DropdownFilterTable
                    filterComponent={[
                      <div>
                        <VoucherPartnerListFilterView
                          setFilterDropdown={setFilterDropdown}
                          filterDropdown={filterDropdown}
                          areaProvinceOptions={areaProvinceOptions}
                          areaDistrictOptions={areaDistrictOptions}
                          selectedAreaDistrict={selectedAreaDistrict}
                          setSelectedAreaDistrict={setSelectedAreaDistrict}
                          selectedAreaProvince={selectedAreaProvince}
                          setSelectedAreaProvince={setSelectedAreaProvince}
                          areaProvinceList={areaProvinceList}
                          areaDistrictList={areaDistrictList}
                          adminRole={adminRole}
                        />
                      </div>
                    ]}
                    resetDefaultTable={() => {
                      const updateFilterDropdown = {
                        ...filterDropdown,
                        page: 0,
                        limit: AppConfigs.defaultPageLimit,
                        role: 'VOUCHER_PARTNER',
                        operatorId: '',
                        status: '',
                        type: '',
                        from: getDateFilterOption(dateFilter.tatCaThoiGian)[0].format('YYYY-MM-DD'),
                        to: getDateFilterOption(dateFilter.tatCaThoiGian)[1].format('YYYY-MM-DD'),
                        selectedValue: dateFilter.tatCaThoiGian,
                        areaId: '',
                        q: '',
                        parentAgencyId: '',
                        createdBy: '',
                      };
                      setFilterDropdown(updateFilterDropdown);
                      setFilter(updateFilterDropdown);
                      Global.g_filterVoucherPartnerList = updateFilterDropdown;
                      setSelectedAreaProvince('');
                      setSelectedAreaDistrict('');
                    }}
                    saveFilter={() => {
                      setFilter(filterDropdown);
                      Global.g_filterVoucherPartnerList = filterDropdown;
                    }}
                    isDefaultFilter={Utils.checkDefaultFilter(
                      ['role', 'page', 'limit', 'from', 'to', 'selectedValue'],
                      filter
                    )}
                  />
                </div>
                {/* cau hinh cot */}
                <DropdownTableConfigColumn
                  columnIds={columnIds}
                  fullColumns={fullColumns}
                  fullColumnIds={fullColumnsIdSorted}
                  onChangeCheckboxValue={onChangeCheckboxValue}
                  saveChanges={saveChanges}
                  resetDefaultTableColumn={() => {
                    setFullColumnIds(defaultColumnIds);
                    resetDefaultTableColumn();
                  }}
                  onChangeFullColumnIds={(ids) => {
                    setFullColumnIds(ids);
                    const key = prefKey + '_FULL_COLUMN_IDS';
                    localStorage.setItem(key, ids.join('|'));
                  }}
                  onSelectAll={onSelectAll}
                  onUnSelectAll={onUnSelectAll}
                />
              </div>
            </div>

            <div className="px-4">
              <KTTable
                columns={columns}
                items={voucherPartnerList}
                selectableRows={false}
                striped={false}
                compactGrid={false}
                onClickedRow={(row) => { }}
                onSelectedRowsChanged={(rows) => { }}
                onDoubleClickedRow={(rows) => { }}
                noDataComponent={
                  <EmptyView
                    icon={imgEmptyNoData}
                    title="Không có dữ liệu để hiển thị"
                    text={<span>Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.</span>}
                  />
                }
              />
            </div>
          </div>

          {/* pagination */}
          <div className="px-4 mt-2">
            <KTPagination
              currentPage={filter.page + 1}
              rowCount={voucherPartnerTotal != 0 ? voucherPartnerTotal : voucherPartnerList?.length}
              rowsPerPage={filter.limit}
              currentItemInPageCount={voucherPartnerList?.length}
              onChangePage={(newPage) => {
                const iNewPage = parseInt(newPage);
                Global.g_filterVoucherPartnerList = {
                  ...Global.g_filterVoucherPartnerList,
                  page: iNewPage - 1,
                };
                setFilter(Global.g_filterVoucherPartnerList);
              }}
              onChangeRowsPerPage={(newRowsPerPage) => {
                const iNewRowsPerPage = parseInt(newRowsPerPage);
                Global.g_filterVoucherPartnerList = {
                  ...Global.g_filterVoucherPartnerList,
                  limit: iNewRowsPerPage,
                  page: 0,
                };
                setFilter(Global.g_filterVoucherPartnerList);
              }}
              color={KTPaginationColor.success}
              appendText="đối tác"
            />
          </div>
        </div>,
        false
      )}
    </div>
  );
}

export default VoucherPartnerListView;
